import {
  REVIEW_ARCHIVE_PAGE_SIZE,
  REVIEW_ARCHIVE_ROLLUP_YEARS,
  REVIEW_SITEMAP_CURRENT_PERIOD_THRESHOLD,
} from "@/lib/reviews/constants"

export interface ReviewArchiveApiReview {
  rating: number
  authorName?: string
  content: string
  created_at: string
  shipment: {
    title: string
  }
  driver: {
    id?: string | number
    name: string
    slug: string
  }
  winningBid: number
}

export interface ReviewArchiveMonthSummary {
  month: string
  total: number
  averageRating?: number | null
}

export interface ReviewLandingApiReview {
  id: string | number
  rating: number
  authorName?: string
  content: string
  date?: string
  created_at?: string
  shipment: {
    title: string
  }
  driver: {
    id: string | number
    name: string
  }
  winningBid: number
}

export interface ReviewLandingApiResponse {
  data: ReviewLandingApiReview[]
  meta: {
    currentPage?: number
    page?: number
    numberOfPages: number
  }
}

interface LandingReviewsProjectionOptions {
  limit?: number
}

export interface ArchiveReview {
  /** Global API position; absent only in synthetic callers and older cached props. */
  position?: number
  rating: number
  authorName?: string
  content: string
  createdAt: string
  shipment: {
    title: string
  }
  driver: {
    name: string
    slug: string
    /** `/drivers/{id}`, present only for Eligible Drivers. */
    profilePath?: string
  }
  winningBid: number
}

export type ReviewArchiveKind = "month" | "year"

export interface ReviewArchiveRoute {
  kind: ReviewArchiveKind
  period: string
  page: number
}

export interface ReviewArchivePeriod {
  kind: ReviewArchiveKind
  period: string
  total: number
}

export interface ReviewArchiveYear {
  readonly year: string
  /** True for a pinned rollup year, served as one year page with no month pages. */
  readonly rollsUp: boolean
  readonly total: number
  /** Newest first, as reported by the inventory, including zero-total months. */
  readonly months: readonly Readonly<ReviewArchiveMonthSummary>[]
}

export interface ReviewArchiveMap {
  /** Newest first. */
  readonly years: readonly ReviewArchiveYear[]
  /** Listing periods, newest first: rollup years plus the months of every other year. */
  periods: ReviewArchivePeriod[]
}

const MONTH_PATTERN = /^(?!0000)(\d{4})-(0[1-9]|1[0-2])$/
const YEAR_PATTERN = /^(?!0000)\d{4}$/
const CONTINUATION_PATTERN = /^[1-9]\d{0,3}$/
const EMAIL_PATTERN = /[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+/gi

const archiveMonthFormatter = new Intl.DateTimeFormat("en-US", {
  month: "long",
  year: "numeric",
  timeZone: "UTC",
})

export function formatArchivePeriodLabel(period: string): string {
  if (!period.includes("-")) {
    return period
  }

  return archiveMonthFormatter.format(new Date(`${period}-01T00:00:00.000Z`))
}

export function parseReviewArchivePath(
  path: string[] | undefined,
): ReviewArchiveRoute | null {
  if (!path || path.length < 1 || path.length > 2) {
    return null
  }

  const period = path[0]
  let kind: ReviewArchiveKind

  if (MONTH_PATTERN.test(period)) {
    kind = "month"
  } else if (YEAR_PATTERN.test(period)) {
    kind = "year"
  } else {
    return null
  }

  let page = 1

  if (path.length === 2) {
    if (!CONTINUATION_PATTERN.test(path[1])) {
      return null
    }

    page = Number(path[1])

    if (!Number.isSafeInteger(page) || page < 2) {
      return null
    }
  }

  return { kind, period, page }
}

/**
 * `/reviews/<period>/1` is the same page as `/reviews/<period>`; return the
 * canonical href so the route can 301 instead of 404. Only well-formed
 * periods redirect; everything else stays with the normal route handling.
 */
export function getArchivePageOneRedirect(
  path: string[] | undefined,
): string | null {
  if (!path || path.length !== 2 || path[1] !== "1") {
    return null
  }

  const period = path[0]

  if (!MONTH_PATTERN.test(period) && !YEAR_PATTERN.test(period)) {
    return null
  }

  return buildArchivePageHref(period, 1)
}

export function redactEmails(value: string): string {
  return value.replace(EMAIL_PATTERN, "[email removed]")
}

export function normalizeReviewAuthorName(value?: unknown): string {
  if (typeof value !== "string") {
    return ""
  }

  const name = redactEmails(value.trim())
  const isUnavailableName =
    name.includes("@") || name.includes("[email removed]")

  if (isUnavailableName) {
    return ""
  }

  return name
}

export function selectNamedReviews<T extends { authorName?: unknown }>(
  reviews: readonly T[],
): { review: T; authorName: string }[] {
  return reviews.flatMap((review) => {
    const authorName = normalizeReviewAuthorName(review.authorName)

    return authorName ? [{ review, authorName }] : []
  })
}

/**
 * Projects an API review into page props. The raw driver id never leaves this
 * function: only an Eligible Driver's profile path is kept, so ineligible ids
 * stay out of the page.
 */
export function projectArchiveReview(
  review: ReviewArchiveApiReview,
  eligibleDriverIds: ReadonlySet<string> = new Set(),
): ArchiveReview {
  const driverId =
    review.driver.id === undefined ? "" : String(review.driver.id)
  const isEligibleDriver = driverId !== "" && eligibleDriverIds.has(driverId)

  return {
    rating: review.rating,
    authorName: normalizeReviewAuthorName(review.authorName),
    content: redactEmails(review.content),
    createdAt: review.created_at,
    shipment: {
      title: redactEmails(review.shipment.title),
    },
    driver: {
      name: redactEmails(review.driver.name),
      slug: redactEmails(review.driver.slug),
      ...(isEligibleDriver && { profilePath: `/drivers/${driverId}` }),
    },
    winningBid: review.winningBid,
  }
}

export function projectLandingReviewsResponse(
  response: ReviewLandingApiResponse,
  options: LandingReviewsProjectionOptions = {},
): ReviewLandingApiResponse {
  const reviews = response.data.slice(0, options.limit)

  return {
    data: reviews.map((review) => ({
      id: review.id,
      rating: review.rating,
      authorName: normalizeReviewAuthorName(review.authorName),
      content: redactEmails(review.content),
      ...(review.date ? { date: review.date } : {}),
      ...(review.created_at ? { created_at: review.created_at } : {}),
      shipment: {
        title: redactEmails(review.shipment.title),
      },
      driver: {
        id: review.driver.id,
        name: redactEmails(review.driver.name),
      },
      winningBid: review.winningBid,
    })),
    meta: {
      ...(response.meta.currentPage !== undefined
        ? { currentPage: response.meta.currentPage }
        : {}),
      ...(response.meta.page !== undefined ? { page: response.meta.page } : {}),
      numberOfPages: response.meta.numberOfPages,
    },
  }
}

export function getArchivePageCount(total: number): number {
  if (total <= 0) {
    return 0
  }

  return Math.ceil(total / REVIEW_ARCHIVE_PAGE_SIZE)
}

/**
 * Global positions (within the period) of the reviews rendered on a page.
 * `renderedCount` is the number of reviews actually on the page, so a final
 * partial page ends at the real position rather than page × page size.
 */
export function getArchivePageRange(
  page: number,
  renderedCount: number,
): { start: number; end: number } {
  const start = (page - 1) * REVIEW_ARCHIVE_PAGE_SIZE + 1

  return { start, end: start + renderedCount - 1 }
}

const utcDateParts = (iso: string) => {
  const date = new Date(iso)

  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth(),
    day: date.getUTCDate(),
    monthName: new Intl.DateTimeFormat("en-US", {
      month: "long",
      timeZone: "UTC",
    }).format(date),
  }
}

/**
 * Human-readable coverage for the reviews rendered on one page, given the
 * createdAt of the two boundary reviews in either order. The archive lists
 * newest-first, but a date range reads chronologically, so this always runs
 * oldest → newest.
 */
export function formatArchiveDateCoverage(
  boundaryA: string,
  boundaryB: string,
): string {
  const [from, to] = [boundaryA, boundaryB]
    .sort((left, right) => Date.parse(left) - Date.parse(right))
    .map(utcDateParts)
  const full = ({ monthName, day, year }: typeof from) =>
    `${monthName} ${day}, ${year}`

  if (from.year !== to.year) {
    return `${full(from)} – ${full(to)}`
  }

  if (from.month !== to.month) {
    return `${from.monthName} ${from.day} – ${to.monthName} ${to.day}, ${from.year}`
  }

  if (from.day !== to.day) {
    return `${from.monthName} ${from.day}–${to.day}, ${from.year}`
  }

  return full(from)
}

export function buildArchivePageHref(period: string, page: number): string {
  const base = `/reviews/${period}`

  if (page <= 1) {
    return base
  }

  return `${base}/${page}`
}

export function isReviewArchiveRollupYear(year: string): boolean {
  return REVIEW_ARCHIVE_ROLLUP_YEARS.includes(year)
}

export function buildReviewArchiveMap(
  months: ReviewArchiveMonthSummary[],
): ReviewArchiveMap {
  const monthsByYear = new Map<string, ReviewArchiveMonthSummary[]>()

  for (const month of months) {
    const year = month.month.slice(0, 4)
    const yearMonths = monthsByYear.get(year) ?? []

    yearMonths.push(month)
    monthsByYear.set(year, yearMonths)
  }

  const years: ReviewArchiveYear[] = []
  const periods: ReviewArchivePeriod[] = []

  for (const year of Array.from(monthsByYear.keys()).sort().reverse()) {
    const yearMonths = monthsByYear.get(year)!

    yearMonths.sort((left, right) => right.month.localeCompare(left.month))

    const rollsUp = isReviewArchiveRollupYear(year)
    const total = yearMonths.reduce((sum, month) => sum + month.total, 0)

    years.push({ year, rollsUp, total, months: yearMonths })

    if (rollsUp) {
      periods.push({ kind: "year", period: year, total })
      continue
    }

    for (const month of yearMonths) {
      periods.push({ kind: "month", period: month.month, total: month.total })
    }
  }

  return { years, periods }
}

/**
 * A month inside a pinned rollup year has no page of its own. When it has
 * reviews, its address permanently redirects to the year page that lists them;
 * otherwise there is nothing to point at and the route stays a 404.
 */
export function getRollupMonthRedirect(
  month: string,
  years: readonly ReviewArchiveYear[],
): string | null {
  const archiveYear = years.find(({ year }) => year === month.slice(0, 4))

  if (!archiveYear?.rollsUp) {
    return null
  }

  const monthHasReviews = archiveYear.months.some(
    (summary) => summary.month === month && summary.total > 0,
  )

  return monthHasReviews ? buildArchivePageHref(archiveYear.year, 1) : null
}

/**
 * The subset of an inventory that has real content and is not in the future.
 * Shared so page rendering and the reviews sitemap can never disagree about
 * which periods exist.
 */
export function selectPublishableArchivePeriods(
  periods: ReviewArchivePeriod[],
  now: Date = new Date(),
): ReviewArchivePeriod[] {
  return periods.filter(
    ({ kind, period, total }) =>
      total > 0 && !isFutureArchiveRoute({ kind, period, page: 1 }, now),
  )
}

export function selectSitemapArchivePeriods(
  periods: ReviewArchivePeriod[],
  now: Date = new Date(),
): ReviewArchivePeriod[] {
  const currentPeriod = { month: formatUtcMonth(now), year: formatUtcYear(now) }

  return selectPublishableArchivePeriods(periods, now).filter(
    ({ kind, period, total }) =>
      period !== currentPeriod[kind] ||
      total > REVIEW_SITEMAP_CURRENT_PERIOD_THRESHOLD,
  )
}

export function getAdjacentArchivePeriods(
  periods: ReviewArchivePeriod[],
  currentPeriod: string,
): { newer: ReviewArchivePeriod | null; older: ReviewArchivePeriod | null } {
  const currentIndex = periods.findIndex(
    ({ period }) => period === currentPeriod,
  )

  if (currentIndex === -1) {
    return { newer: null, older: null }
  }

  return {
    newer: periods[currentIndex - 1] ?? null,
    older: periods[currentIndex + 1] ?? null,
  }
}

export function formatUtcYear(now: Date = new Date()): string {
  return String(now.getUTCFullYear()).padStart(4, "0")
}

export function formatUtcMonth(now: Date = new Date()): string {
  return `${formatUtcYear(now)}-${String(now.getUTCMonth() + 1).padStart(
    2,
    "0",
  )}`
}

export function isFutureArchiveRoute(
  route: ReviewArchiveRoute,
  now: Date = new Date(),
): boolean {
  if (route.kind === "year") {
    return route.period > formatUtcYear(now)
  }

  return route.period > formatUtcMonth(now)
}
